import { useApiMutation, useApiQuery } from './useApi';
import { useQueryClient } from 'react-query';
import { invalidateCrmQueries } from './useCrm';

// #################################################
//                      TYPES
// #################################################

export type CrmEmailStatus = 'pending' | 'attached' | 'dismissed' | 'excluded';

export type CrmEmailSuggestion = {
    type: 'facility' | 'prospect';
    id: number;
    name: string | null;
    reason: string;
};

/** Un email de la file de reprise (décision #46 de la carte #42). */
export type CrmQueueEmail = {
    id: number;
    direction: 'inbound' | 'outbound';
    from_email: string;
    from_name: string | null;
    to_emails: string[] | null;
    cc_emails: string[] | null;
    subject: string | null;
    excerpt: string;
    sent_at: string;
    status: CrmEmailStatus;
    matched_by: string | null;
    mailboxes: string[];
    suggestions?: CrmEmailSuggestion[];
};

export type CrmEmailCounts = {
    pending: number;
    dismissed: number;
    attached: number;
    attached_last_30d: number;
    auto_rate_last_30d: number;
    exclusions: number;
};

export type CrmEmailExclusion = {
    id: number;
    type: 'domain' | 'address';
    value: string;
    blocked_count: number;
    created_at: string;
    creator?: { id: number; email: string; firstname?: string | null; lastname?: string | null } | null;
};

export type InternalMailbox = {
    id: number;
    name: string;
    email_address: string;
    provider: string;
    is_active: boolean;
    needs_reconnection: boolean;
    last_sync_at: string | null;
    last_error: string | null;
    last_error_at: string | null;
};

/** Un item du fil d'activité unifié d'une fiche (décisions #47/#52). */
export type CrmFeedItem = {
    kind: 'email' | 'note' | 'reminder' | 'intercom';
    id: number;
    date: string;
    /** Fiche client : élément venu de la fiche du prospect converti en ce client. */
    from_prospect?: { id: number; name: string };
    // intercom
    title?: string | null;
    state?: string | null;
    parts_count?: number;
    source_url?: string | null;
    intercom_url?: string;
    author_email?: string | null;
    // email
    direction?: 'inbound' | 'outbound';
    from_email?: string;
    from_name?: string | null;
    to_emails?: string[] | null;
    cc_emails?: string[] | null;
    subject?: string | null;
    body_html?: string | null;
    body_text?: string | null;
    excerpt?: string;
    message_id?: string;
    attachments?: { name?: string; filename?: string; size?: number; url?: string; path?: string }[] | null;
    mailboxes?: string[];
    thread_key?: string;
    /** Fournisseur de la boîte source d'un email : google / microsoft / smtp. */
    provider?: string | null;
    /** Système d'origine quand l'item en vient un (ex. 'noota' pour un compte rendu). */
    source?: string | null;
    // note
    note_type?: string;
    content?: string;
    author?: string | null;
    scheduled_at?: string | null;
    reminder_at?: string | null;
    duration_minutes?: number | null;
    tags?: { id: number; name: string; color?: string | null }[];
};

export type CrmFeed = {
    upcoming: CrmFeedItem[];
    items: CrmFeedItem[];
};

// #################################################
//                      QUERIES
// #################################################

export function useCrmEmailQueue(filters: { status?: string; mailbox_id?: number; q?: string } = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') params.append(key, String(value));
    });
    const query = params.toString();

    return useApiQuery<{ data: CrmQueueEmail[]; total: number }>(
        `/crm/crm-emails${query ? `?${query}` : ''}`
    );
}

/**
 * Le corps d'un email, chargé au dépliage d'une ligne.
 *
 * Volontairement hors de `useCrmEmailQueue` : à 1 500 emails en attente et
 * plusieurs Ko de corps chacun, les embarquer dans la liste ferait plusieurs
 * mégaoctets par page — pour un contenu qu'on ne lit que sur quelques lignes.
 */
export type CrmEmailBody = {
    id: number;
    body_html: string | null;
    body_text: string | null;
    to_emails: string[] | null;
    cc_emails: string[] | null;
};

export function useCrmEmailBody(emailId: number | null) {
    return useApiQuery<CrmEmailBody>(
        emailId ? `/crm/crm-emails/${emailId}` : '',
        {},
        { enabled: !!emailId, staleTime: 5 * 60 * 1000 }
    );
}

export function useCrmEmailCounts(enabled = true, refetchInterval: number | false = 60000) {
    return useApiQuery<CrmEmailCounts>('/crm/crm-emails/counts', {}, {
        enabled,
        refetchInterval,
    });
}

export function useCrmEmailExclusions() {
    return useApiQuery<{ data: CrmEmailExclusion[] }>('/crm/crm-email-exclusions');
}

export function useInternalMailboxes() {
    return useApiQuery<{ data: InternalMailbox[] }>('/crm/internal-mailboxes');
}

/** Alimente le bandeau « boîte déconnectée » affiché en continu (décision #50). */
export function useInternalMailboxHealth(enabled = true) {
    return useApiQuery<{ disconnected: { id: number; email_address: string; since: string | null }[] }>(
        '/crm/internal-mailboxes/health',
        {},
        { enabled, refetchInterval: 120000 }
    );
}

export type IntercomMessage = {
    author: string | null;
    author_type: string | null;
    body_html: string | null;
    at: number | null;
    attachments: string[];
};

export type IntercomThread = {
    id: number;
    state: string | null;
    intercom_url: string;
    messages: IntercomMessage[];
};

/**
 * Le fil complet d'une conversation Intercom, chargé à la demande (au dépliage).
 * Les messages ne sont pas stockés côté TriggerFlow : Intercom reste la source
 * de vérité, le backend met simplement la réponse en cache quelques minutes.
 */
export function useIntercomThread(conversationId: number | null) {
    return useApiQuery<IntercomThread>(
        conversationId ? `/crm/intercom-conversations/${conversationId}` : '',
        {},
        { enabled: !!conversationId, staleTime: 5 * 60 * 1000 }
    );
}

/** Le fil d'activité unifié d'une fiche : emails + notes + rappels + RDV. */
export function useCrmFeed(type: 'facility' | 'prospect', id: number) {
    const path = type === 'facility' ? `/crm/facilities/${id}/feed` : `/crm/prospects/${id}/feed`;

    return useApiQuery<CrmFeed>(path, {}, { enabled: id > 0 });
}

// #################################################
//                     MUTATIONS
// #################################################

/**
 * Associe un email à une fiche. `learn_domain` (activé par défaut côté API)
 * nourrit la table de correspondances COMMUNE avec Noota : emails et
 * réunions du même domaine se rattacheront seuls ensuite.
 */
export function useAssociateCrmEmail() {
    const queryClient = useQueryClient();

    return useApiMutation<CrmQueueEmail, Error, { emailId: number; type: 'facility' | 'prospect'; id: number; learn_domain?: boolean }>(
        'post',
        (vars) => `/crm/crm-emails/${vars.emailId}/associate`,
        { onSuccess: () => invalidateCrmQueries(queryClient) }
    );
}

export function useDismissCrmEmail() {
    const queryClient = useQueryClient();

    return useApiMutation<{ message: string }, Error, { emailId: number }>(
        'post',
        (vars) => `/crm/crm-emails/${vars.emailId}/dismiss`,
        { onSuccess: () => invalidateCrmQueries(queryClient) }
    );
}

/** Exclut l'expéditeur (adresse ou domaine entier) : coupe l'ingestion future. */
export function useExcludeCrmEmail() {
    const queryClient = useQueryClient();

    return useApiMutation<{ message: string; swept: number }, Error, { emailId: number; scope: 'address' | 'domain' }>(
        'post',
        (vars) => `/crm/crm-emails/${vars.emailId}/exclude`,
        { onSuccess: () => invalidateCrmQueries(queryClient) }
    );
}

export function useStoreCrmEmailExclusion() {
    const queryClient = useQueryClient();

    return useApiMutation<CrmEmailExclusion, Error, { type: 'domain' | 'address'; value: string }>(
        'post',
        () => '/crm/crm-email-exclusions',
        { onSuccess: () => invalidateCrmQueries(queryClient) }
    );
}

export function useDeleteCrmEmailExclusion() {
    const queryClient = useQueryClient();

    return useApiMutation<null, Error, { exclusionId: number }>(
        'delete',
        (vars) => `/crm/crm-email-exclusions/${vars.exclusionId}`,
        { onSuccess: () => invalidateCrmQueries(queryClient) }
    );
}

/** Lance la connexion OAuth d'une boîte interne : renvoie l'URL à ouvrir en popup. */
export function useConnectInternalMailbox() {
    const queryClient = useQueryClient();

    return useApiMutation<{ auth_url: string }, Error, { name?: string; mailbox_id?: number }>(
        'post',
        () => '/crm/internal-mailboxes/connect/google',
        { onSuccess: () => invalidateCrmQueries(queryClient) }
    );
}

export function useDisconnectInternalMailbox() {
    const queryClient = useQueryClient();

    return useApiMutation<{ message: string }, Error, { mailboxId: number }>(
        'post',
        (vars) => `/crm/internal-mailboxes/${vars.mailboxId}/disconnect`,
        { onSuccess: () => invalidateCrmQueries(queryClient) }
    );
}
